<template>
  <TabsLayout>
    <div
      :class="isLoading ? 'tw-bg-[#D9D9D9]' : ' tw-bg-white'"
      class="tabs-selector md:tw-border tw-rounded-md tw-border-gray-200"
      style="
        width: 100%;
        max-width: 100%;
        box-sizing: border-box;
        overflow: visible;
        max-height: none;
        padding: 20px;
      "
    >
      <div
        class="step-tabs"
        style="
          display: flex;
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
          align-items: stretch;
          justify-content: center;
          overflow: hidden;
          margin: 15px 0 0 0;
          padding: 0;
        "
      >
        <button
          v-for="(item, index) in items"
          :key="item.name"
          type="button"
          class="step-tab"
          :class="{
            'step-tab--selected': isSelected(item),
            'step-tab--disabled': !steps.includes(item.name),
          }"
          :style="tabStyle(index, item)"
          :disabled="!steps.includes(item.name)"
          @click="onTabClick(item)"
          @mouseenter="hoveredTab = item.name"
          @mouseleave="hoveredTab = null"
        >
          <v-icon class="tw-shrink-0" :icon="item.icon" size="18" style="margin-right: 8px" />
          <span class="step-tab__label">{{ item.name }}</span>
        </button>
      </div>

      <div
        v-if="isLoading"
        class="tw-w-full tw-px-4"
      >
        <div class="tw-h-[5px] tw-w-full tw-bg-zinc-400 tw-overflow-hidden">
          <div class="progress tw-w-full tw-h-full tw-bg-black left-right" />
        </div>
      </div>

      <div style="margin-top: 13px">
        <Models v-if="currentStep === 'Models'" @next-step="nextStep" />
        <Trims v-else-if="currentStep === 'Trims'" @next-step="nextStep" />
        <Options
          v-else-if="currentStep === 'Options' || currentStep === 'Request'"
          @next-step="nextStep"
        />
        <RequestOrderDialog @toggle-request-order="toggleRequestOrder" v-model="requestOrder" />
      </div>
    </div>
  </TabsLayout>
</template>

<script setup>
import { onMounted, ref, watch } from 'vue';
import Models from '@/components/home/Models.vue';
import Trims from '@/components/home/Trims.vue';
import Options from '@/components/home/Options.vue';
import TabsLayout from '@/components/layouts/TabsLayout.vue';
import { useCarsStore } from '@/stores/useCarsStore';
import { storeToRefs } from 'pinia';
import { useVariablesStore } from '@/stores/useVariablesStore';
import RequestOrderDialog from '@/components/common/Dialog/RequestOrderDialog.vue';
import { useSelectionQuery } from '@/composables/useSelectionQuery';

const CHEVRON = 18;
const TAB_OVERLAP = 16;
const COLOR_SELECTED = '#000000';
const COLOR_HOVER = '#2a2a2a';
const COLOR_PASSED = '#eeeeee';
const COLOR_LATER = '#e5e5e5';

const { isLoading } = storeToRefs(useCarsStore());
const { requestOrder, steps, currentStep } = storeToRefs(useVariablesStore());
const variablesStore = useVariablesStore();
const { ensureHydrated, watchSelections } = useSelectionQuery();

const toggleRequestOrder = () => {
  requestOrder.value = !requestOrder.value;
};

const items = ref([
  { name: 'Models', id: 1, icon: 'fa fa-shield', isActive: false },
  { name: 'Trims', id: 2, icon: 'fa fa-check-square', isActive: false },
  { name: 'Options', id: 3, icon: 'fa fa-th-large', isActive: false },
  { name: 'Request', id: 4, icon: 'fa fa-bookmark', isActive: false },
]);

const hoveredTab = ref(null);
const isSelected = (item) => currentStep.value === item.name;

function tabClipPath(index) {
  const last = items.value.length - 1;
  const c = CHEVRON;
  if (index === 0) {
    return `polygon(0 0, calc(100% - ${c}px) 0, 100% 50%, calc(100% - ${c}px) 100%, 0 100%)`;
  }
  if (index === last) {
    return `polygon(0 0, calc(100% - ${c}px) 0, 100% 50%, calc(100% - ${c}px) 100%, 0 100%, ${c}px 50%)`;
  }
  return `polygon(0 0, calc(100% - ${c}px) 0, 100% 50%, calc(100% - ${c}px) 100%, 0 100%, ${c}px 50%)`;
}

function tabBackground(index, item) {
  if (isSelected(item)) return COLOR_SELECTED;
  if (hoveredTab.value === item.name && steps.value.includes(item.name)) return COLOR_HOVER;
  const currentIndex = items.value.findIndex((entry) => entry.name === currentStep.value);
  if (index < currentIndex) return COLOR_PASSED;
  return COLOR_LATER;
}

function tabStyle(index, item) {
  const selected = isSelected(item);
  const hovered = !selected && hoveredTab.value === item.name && steps.value.includes(item.name);
  const count = items.value.length;
  const flexBasis = `calc((100% + ${(count - 1) * TAB_OVERLAP}px) / ${count})`;
  return {
    flex: `1 1 ${flexBasis}`,
    width: flexBasis,
    maxWidth: '256px',
    minWidth: '0',
    boxSizing: 'border-box',
    height: '48px',
    minHeight: '48px',
    maxHeight: '48px',
    marginLeft: index === 0 ? '0' : `-${TAB_OVERLAP}px`,
    transform: index === 0 ? 'none' : 'translateX(2px)',
    paddingLeft: index === 0 ? '16px' : `${16 + CHEVRON}px`,
    paddingRight: `${16 + CHEVRON}px`,
    clipPath: tabClipPath(index),
    WebkitClipPath: tabClipPath(index),
    backgroundColor: tabBackground(index, item),
    color: selected || hovered ? '#ffffff' : '#000000',
  };
}

const onTabClick = (item) => {
  if (!steps.value.includes(item.name)) return;
  variablesStore.setCurrentStep(item.name);
};

const nextStep = () => {
  variablesStore.goToNextStep();
};

watch(
  () => currentStep.value,
  (newVal) => {
    if (newVal === 'Request') {
      requestOrder.value = true;
    }
  },
);
watch(
  () => requestOrder.value,
  (newVal) => {
    if (!newVal && currentStep.value === 'Request') {
      variablesStore.setCurrentStep('Options');
    }
  },
);

onMounted(async () => {
  await ensureHydrated();
  watchSelections();
});
</script>

<style>
/* Fallback styles — critical geometry is inline for shadow DOM */
.step-tabs {
  position: relative;
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.step-tab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  font: inherit;
  position: relative;
  box-sizing: border-box;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.step-tab__label {
  display: inline;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.875rem;
  line-height: 1.25;
}

/* Hover: black, but a bit lighter than selected */
.step-tab:hover:not(.step-tab--disabled):not(.step-tab--selected) {
  background-color: #2a2a2a !important;
  color: #ffffff !important;
}

.step-tab--selected {
  background-color: #000000 !important;
  color: #ffffff !important;
}

.step-tab--selected:hover {
  background-color: #000000 !important;
  color: #ffffff !important;
}

.step-tab--disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.progress {
  animation: progress 1s infinite linear;
}

.left-right {
  transform-origin: 0% 50%;
}
@keyframes progress {
  0% {
    transform: translateX(0) scaleX(0);
  }
  40% {
    transform: translateX(0) scaleX(0.4);
  }
  100% {
    transform: translateX(100%) scaleX(0.5);
  }
}
</style>
